<script module lang="ts">
  let counter = 0;
</script>

<script lang="ts">
  import type { Interval } from "../type";
  import { type FeatureTrackingReference, featureTrackingReferenceAttributes } from "../../tracking";
  import { isActive } from "../../../toggle/Toggle.ts";

  interface Props {
    range?: Interval;
    unit?: string;
    availableRange?: Required<Interval>;
    featureTrackingReference?: FeatureTrackingReference;
    isValid: boolean;
  }

  let { unit, range = $bindable(), availableRange, featureTrackingReference, isValid }: Props = $props();

  let isMigrateOcTextFieldActive = $derived(isActive("HEUREKA_2220_MIGRATE_TO_OC_TEXT_FIELD"));

  const minInputId = `hc_minMax__min${counter}`;
  const maxInputId = `hc_minMax__max${counter++}`;

  let valueMin = $derived(range?.from || availableRange?.from);
  let valueMax = $derived(range?.to || availableRange?.to);

  const defaultValueMin = range?.from;
  const defaultValueMax = range?.to;

  let labelSuffix = (unit && ` (${unit})`) || "";

  function onUpdateMinValue(event: Event) {
    if (event.target instanceof HTMLInputElement) {
      const from = event.target.valueAsNumber;
      if (range) {
        range.from = isNaN(from) ? undefined : from;
      }
    }
  }

  /*                                                                   */
  /*                                                                                                  */
  function onUpdateMinValueOc(event: Event) {
    const target = event.target as HTMLElement & { value?: string };
    const from = Number(target.value);
    if (range) {
      range.from = target.value === "" || isNaN(from) ? undefined : from;
    }
  }

  function onUpdateMaxValueOc(event: Event) {
    const target = event.target as HTMLElement & { value?: string };
    const to = Number(target.value);
    if (range) {
      range.to = target.value === "" || isNaN(to) ? undefined : to;
    }
  }

  function onUpdateMaxValue(event: Event) {
    if (event.target instanceof HTMLInputElement) {
      const to = event.target.valueAsNumber;
      if (range) {
        range.to = isNaN(to) ? undefined : to;
      }
    }
  }
</script>

{#if isMigrateOcTextFieldActive}
  <div class="hc_minMax hc_minMax--oc">
    <oc-text-field-v1
      id={minInputId}
      maxlength="5"
      type="number"
      inputmode="numeric"
      placeholder={availableRange?.from?.toFixed() || ""}
      value={range?.from ? String(range.from) : ""}
      variant={isValid ? "default" : "error"}
      without-optional-label
      data-default-value={defaultValueMin ? defaultValueMin : ""}
      data-ts-feature-filter-method="interval"
      onchange={onUpdateMinValueOc}
      {...featureTrackingReferenceAttributes(featureTrackingReference)}>Von{labelSuffix}</oc-text-field-v1
    >
    <oc-text-field-v1
      id={maxInputId}
      maxlength="5"
      type="number"
      inputmode="numeric"
      placeholder={availableRange?.to?.toFixed() || ""}
      value={range?.to ? String(range.to) : ""}
      variant={isValid ? "default" : "error"}
      without-optional-label
      data-default-value={defaultValueMax ? defaultValueMax : ""}
      data-ts-feature-filter-method="interval"
      onchange={onUpdateMaxValueOc}
      {...featureTrackingReferenceAttributes(featureTrackingReference)}>Bis{labelSuffix}</oc-text-field-v1
    >
  </div>
{:else}
  <div class="hc_minMax pl_input--floating-label pl_input--hide-placeholders">
    <span class="js-pl_input pl_input">
      <input
        id={minInputId}
        maxlength="5"
        class="pl_input__field {isValid ? '' : 'pl_input__field--invalid'}"
        type="number"
        inputmode="numeric"
        placeholder={availableRange?.from?.toFixed() || ""}
        max={valueMax}
        value={range?.from ? range.from : ""}
        data-default-value={defaultValueMin ? defaultValueMin : ""}
        data-ts-feature-filter-method="interval"
        onchange={onUpdateMinValue}
        {...featureTrackingReferenceAttributes(featureTrackingReference)}
      />
      <label for={minInputId} class="js-pl_label pl_label {isValid ? '' : 'pl_label--invalid'}">
        Von{labelSuffix}
        {#if !isValid}
          <oc-icon-v1 class="icon" type="clear" size="50"></oc-icon-v1>
        {/if}
      </label>
    </span>
    <span class="js-pl_input pl_input">
      <input
        id={maxInputId}
        maxlength="5"
        class="pl_input__field {isValid ? '' : 'pl_input__field--invalid'}"
        type="number"
        inputmode="numeric"
        placeholder={availableRange?.to?.toFixed() || ""}
        min={valueMin}
        value={range?.to ? range.to : ""}
        data-default-value={defaultValueMax ? defaultValueMax : ""}
        data-ts-feature-filter-method="interval"
        onchange={onUpdateMaxValue}
        {...featureTrackingReferenceAttributes(featureTrackingReference)}
      />
      <label for={maxInputId} class="js-pl_label pl_label {isValid ? '' : 'pl_label--invalid'}">
        Bis{labelSuffix}
        {#if !isValid}
          <oc-icon-v1 class="icon" type="clear" size="50"></oc-icon-v1>
        {/if}
      </label>
    </span>
  </div>
{/if}

<style lang="scss">
  .hc_minMax {
    margin: 0;
    width: 100%;
    display: flex;
    gap: var(--oc-semantic-spacing-50);
    /*                                      */
    padding-top: var(--oc-semantic-spacing-50);

    > * {
      flex: 1;
      /*                                                      */
      min-width: 0;
      margin: 0;
    }
  }

  .hc_minMax--oc {
    /*                              */
    padding-left: var(--oc-semantic-spacing-12);
    padding-right: var(--oc-semantic-spacing-12);
  }

  .pl_input__field--invalid {
    border-color: var(--oc-semantic-color-error-100) !important;
  }

  .pl_label--invalid {
    color: var(--oc-semantic-color-error-100) !important;

    .icon {
      display: inline-block;
      width: 16px;
      height: 16px;
      margin-left: 6px;
      vertical-align: middle;
      --color: var(--oc-semantic-color-error-100);
    }
  }
</style>
